import { usePartner } from '@/context/PartnerContext'
import { Flex, FormControl, FormErrorMessage, FormLabel, Input, Stack, Text } from '@chakra-ui/react'
import type { AcquisitionNumber, EditOrganizationUnit } from 'BrokerTsLib/src/dtos'
import { AcquisitionNumberType } from 'BrokerTsLib/src/enums'
import { useController, useFieldArray, useFormContext } from 'BrokerTsLib/src/rhf'
import type { Ref } from 'react'
import React, { forwardRef, useState } from 'react'

import type { FormSectionProps } from '@/containers/forms/FormSection'
import { FormSection } from '@/containers/forms/FormSection'
import { useAcquisitionNumber } from '@/hooks/useAcquisitionNumbers'

export const OrganizationAcquisitionNumbersFormSection = forwardRef(
  function OrganizationAcquisitionNumbersFormSection(props: FormSectionProps, ref: Ref<HTMLDivElement>) {
    const { control, register } = useFormContext<EditOrganizationUnit>()

    const { fields } = useFieldArray({
      name: 'acquisitionNumbers',
      control,
    })

    const { currentPartner } = usePartner()

    return <FormSection ref={ref} sectionTitle='Ziskatelská čísla' {...props}>
      <Stack gap={6}>
        {fields.map((field, index) => <DividedAcquisitionNumberFormControl key={field.id} acquisitionNumber={field} name={`acquisitionNumbers.${index}.acquisitionNumber`} />)}

        {currentPartner?.partner === 'Cebia' &&
                    <FormControl>
                      <FormLabel fontWeight={'bold'}>
                        Allianz Travel
                      </FormLabel>
                      <Input
                        {...register('properties.allianzTravelAgentCode')}
                      />
                    </FormControl>
        }
      </Stack>
    </FormSection>
  }
)

const DividedAcquisitionNumberFormControl = ({ acquisitionNumber, name }: { acquisitionNumber: AcquisitionNumber, name: string }) => {
  const { segments } = useAcquisitionNumber(acquisitionNumber.insurerId, acquisitionNumber.acquisitionNumber)
  const { field: { onChange, value, name: inputName }, fieldState: { error } } = useController({
    name,
    rules: {
      maxLength: 256
    }
  })

  return (
    <FormControl>
      <FormLabel fontWeight={'bold'} htmlFor={name}>
        {acquisitionNumber.insurerName}
        {acquisitionNumber.type === AcquisitionNumberType.ShortInsuranceOrganizationUnitId && ' (Denní POV)'}
      </FormLabel>

      <DividedAcquisitionNumberInput
        value={value}
        onChange={onChange}
        name={inputName}
        segments={segments}
        isInvalid={!!error}
      />

      <FormErrorMessage>{error?.message}</FormErrorMessage>
    </FormControl>
  )
}


const DividedAcquisitionNumberInput = ({ value, onChange, name, segments, isInvalid }: {
  value?: string
  onChange: (value: string) => void
  name: string
  segments: { label?: string }[]
  isInvalid?: boolean
}) => {
  const isDivided = segments.length > 1
  const [inputValue, setInputValue] = useState<string[]>(isDivided ? (value ?? '').split('/').slice(0, segments.length) : [value ?? ''])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    setInputValue(value => isDivided ? [...value.slice(0, index), e.target.value, ...value.slice(index + 1)] : [e.target.value])
  }

  const handleInputBlur = () => {
    onChange(inputValue.join('/'))
  }

  return <Flex w={'full'} gap={2}>
    {segments.map((segment, index) => <React.Fragment key={index}>
      <FormControl variant='fullWidth' isInvalid={isInvalid}>
        {segment.label &&
                    <FormLabel htmlFor={name}>
                      {segment.label}
                    </FormLabel>}
        <Input
          name={name}
          value={inputValue[index]}
          onChange={(e) => handleInputChange(e, index)}
          onBlur={handleInputBlur}
          placeholder={segment.label}
        />
      </FormControl>
      {index !== (segments.length - 1) && <Text alignSelf={'end'} mb={2}>/</Text>}
    </React.Fragment>)}
  </Flex>
}
